import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusIcon } from "@heroicons/react/20/solid";

import ColumnPersonInformationAvatar from "../molecules/ColumnPersonInformationAvatar";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { TableLoader } from "../molecules/TableLoader";
import ColumnText from "../molecules/ColumnText";
import PaginationUI from "../atoms/PaginationUI";
import { Empty } from "./Empty";

import { QueryMetaData } from "@/app/gyms/gyms";
import { DOMAIN } from "@/app/app.api";

type CompanieProps = {
  id: number | string;
  name: string;
  image: string;
  branchs: string;
  users: string;
  status: number;
};

export type TableListCompaniesProps = {
  companies: CompanieProps[];
  handlePageChange: (page: number) => void;
  handleEntriesChange: (entries: number) => void;
  entries: number;
  queryMetaData: QueryMetaData;
  title: string;
  description: string;
  onClickTop: () => void;
  isLoading: boolean;
  onDelete: (id: string | number | null) => void;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEdit: (id: string | number | null) => void;
  loadingActions: boolean;
  canCreateEditCompany?: boolean;
  canDeleteCompany?: boolean;
};

const TableListCompanies: React.FC<TableListCompaniesProps> = ({
  title,
  description,
  companies,
  onClickTop,
  isLoading,
  onDelete,
  searchQuery,
  onSearchChange,
  onEdit,
  loadingActions,
  handlePageChange,
  handleEntriesChange,
  entries,
  queryMetaData,
  canCreateEditCompany,
  canDeleteCompany,
}) => {
  const navigate = useNavigate();
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="flex gap-2 justify-between items-center">
        <div className="sm:flex w-full sm:max-w-sm">
          <InputWithCornerHint
            label={t("gym.views.search")}
            id="search"
            name="search"
            type="text"
            className="w-full"
            placeholder={t("gym.views.camera")}
            value={searchQuery}
            onChange={onSearchChange}
          />
        </div>

        {canCreateEditCompany && (
          <div className="sm:flex mt-4">
            <button
              onClick={onClickTop}
              type="button"
              className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
            >
              <PlusIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <Fragment>
                <TableLoader columns={12} rows={14} />
              </Fragment>
            ) : companies.length === 0 ? (
              <div className="mt-4">
                <Empty texto={t("gym.views.no_gyms")} />
              </div>
            ) : (
              <Fragment>
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                      >
                        {t("gym.views.name")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("gym.views.branchs")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("gym.views.users")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {companies.map((company) => (
                      <tr key={company.id}>
                        <div
                          onClick={() =>
                            navigate(`/branchs?gymId=${company.id}`)
                          }
                          className="cursor-pointer"
                        >
                          <ColumnPersonInformationAvatar
                            id={company.id as number}
                            name={company.name}
                            email={""}
                            image={`${DOMAIN}/uploads/gyms/${company.image}`}
                            fallbackSrc="https://test.fitmewise.com/uploads/branchs/default.png"
                          />
                        </div>
                        <ColumnText title={company.branchs} />
                        <ColumnText title={company.users} />
                        <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0 space-x-2">
                          {canCreateEditCompany && (
                            <a
                              onClick={() => onEdit(company.id)}
                              href="#"
                              className="text-primary-600 hover:text-primary-900 mr-4"
                            >
                              {t("general.edit")}
                            </a>
                          )}
                          {canDeleteCompany && (
                            <a
                              onClick={() => {
                                onDelete(company.id);
                              }}
                              href="#"
                              className="text-red-600 hover:text-red-900"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Fragment>
            )}
          </div>
        </div>
        <PaginationUI
          onPageChange={(event) => handlePageChange(event)}
          currentPage={queryMetaData.page}
          pageSize={queryMetaData.perPage}
          totalResults={queryMetaData.total}
          totalPages={queryMetaData.lastPage}
        />
      </div>
    </div>
  );
};

export default TableListCompanies;
